import { Inbox } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Candidate } from '../dealroom/types.js';
import { useTRPC } from '../trpc.js';
import { CandidateCard, type CandidateExample } from './CandidateCard.js';
import type { SwipeDirection } from './useSwipe.js';

/** How long the card takes to leave, and therefore how long the next one waits. */
const FLY_MS = 280;

/** Keep this many undecided cards in hand before asking for another page. */
const DECK_LOW_WATER = 5;

export interface CandidateDeckProps {
  rows: readonly Candidate[];
  onDecide: (candidate: Candidate, decision: 'accept' | 'ignore') => void;
  onSkip: (candidate: Candidate) => void;
  onUndo: () => void;
  canUndo: boolean;
  onNeedMore: () => void;
  empty: React.ReactNode;
}

/**
 * One candidate at a time, decided with a hand or with four keys. The list view
 * is for reading the shape of the backlog; this is for getting through it.
 */
export function CandidateDeck(props: CandidateDeckProps): React.JSX.Element {
  const trpc = useTRPC();
  const stillAnimating = useRef<number | null>(null);
  const [leaving, setLeaving] = useState<{
    candidate: Candidate;
    direction: SwipeDirection;
  } | null>(null);
  const calm = useReducedMotion();

  const [top, ...rest] = props.rows;
  const peeks = rest.slice(0, 2);

  // Only the card being decided asks what its companies do: the two behind it
  // are edges, and the answer for them would be thrown away.
  const names = useMemo(() => top?.exampleCompanies ?? [], [top]);
  const examples = useQuery(trpc.candidates.examples.queryOptions({ names: [...names] }));
  const tldrs: readonly CandidateExample[] = useMemo(() => {
    const found = new Map(
      (examples.data?.companies ?? []).map((company) => [company.name, company]),
    );
    return names.map((name) => ({
      name,
      tldr: found.get(name)?.tldr ?? null,
      website: found.get(name)?.website ?? null,
    }));
  }, [names, examples.data]);

  const { onDecide, onSkip, onNeedMore, rows } = props;
  useEffect(() => {
    if (rows.length < DECK_LOW_WATER) onNeedMore();
  }, [rows.length, onNeedMore]);

  useEffect(
    () => () => {
      if (stillAnimating.current !== null) window.clearTimeout(stillAnimating.current);
    },
    [],
  );

  const send = useCallback(
    (direction: SwipeDirection) => {
      const card = rows[0];
      if (card === undefined || stillAnimating.current !== null) return;
      // The decision is made now; the card flying off is what it looks like,
      // not what it waits for.
      if (direction === 'right') onDecide(card, 'accept');
      else if (direction === 'left') onDecide(card, 'ignore');
      else onSkip(card);

      if (calm) return;
      setLeaving({ candidate: card, direction });
      stillAnimating.current = window.setTimeout(() => {
        stillAnimating.current = null;
        setLeaving(null);
      }, FLY_MS);
    },
    [rows, onDecide, onSkip, calm],
  );

  const { onUndo, canUndo } = props;
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === 'arrowright' || key === 'a') send('right');
      else if (key === 'arrowleft' || key === 'i') send('left');
      else if (key === 'arrowup' || key === 's') send('up');
      else if (key === 'z') {
        if (canUndo) onUndo();
        else return;
      } else return;
      event.preventDefault();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [send, onUndo, canUndo]);

  return (
    <div className="decide-host">
      <div className="decide-deck">
        {[...peeks].reverse().map((candidate, index) => (
          <CandidateCard
            key={candidate.id}
            candidate={candidate}
            examples={[]}
            depth={peeks.length - index}
            interactive={false}
            leaving={null}
            onSwipe={send}
          />
        ))}

        {top !== undefined && (
          <CandidateCard
            key={top.id}
            candidate={top}
            examples={tldrs}
            depth={0}
            interactive={leaving === null}
            leaving={null}
            onSwipe={send}
          />
        )}

        {leaving !== null && (
          <CandidateCard
            key={`leaving-${leaving.candidate.id}`}
            candidate={leaving.candidate}
            examples={[]}
            depth={0}
            interactive={false}
            leaving={leaving.direction}
            onSwipe={send}
          />
        )}

        {top === undefined && (
          <div className="decide-empty empty-state">
            <span className="empty-state-icon" aria-hidden="true">
              <Inbox />
            </span>
            {props.empty}
          </div>
        )}
      </div>

      <p className="decide-keys subtle">
        <span>
          <kbd>→</kbd> or <kbd>A</kbd> track fund
        </span>
        <span>
          <kbd>←</kbd> or <kbd>I</kbd> dismiss
        </span>
        <span>
          <kbd>↑</kbd> or <kbd>S</kbd> skip
        </span>
        <span>
          <kbd>Z</kbd> undo
        </span>
      </p>
    </div>
  );
}

/** Somebody who asked for less movement gets the decision without the throw. */
function useReducedMotion(): boolean {
  const [calm, setCalm] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listen = (): void => setCalm(query.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);
  return calm;
}
